import { useEffect, useState } from 'react';
import { currentUid, handoffForMove } from '../lib/account';
import { withHandoff, type HandoffVia } from '../lib/handoff';
import { chromeIntentUrl, inAppBrowser, platform } from '../lib/in-app-browser';
import { MOVE, browserName } from '../data/keep-copy';
import { track } from '../analytics';

/**
 * The road, carried out of Facebook's (or Instagram's) browser into the phone's
 * own, now, without waiting on the email: a one-time handoff code for this
 * anonymous uid (lib/handoff.ts) on a link to the road. The browser that opens
 * it signs in as the same uid before boot looks for anyone new (App.tsx), so it
 * lands on their road, not the intro.
 *
 * - iOS: nothing on a page can open Safari from in there. The ⋯ menu's "Open in
 *   external browser" opens what the address bar says, so the link goes there.
 * - Android: an intent:// link opens Chrome directly.
 * - Both: the link can be copied, for pasting by hand.
 *
 * `handoff_offered` when the way is shown, `handoff_tapped` on the ways a page
 * can see (the menu's tap it cannot), `handoff_landed` in the other browser,
 * all three with the same uid.
 */
export function MoveToBrowser({ from }: { from: 'reward' | 'feed' | 'put-me-in' }) {
  const os = platform();
  const kind = inAppBrowser();
  const browser = browserName(os);
  // undefined while it is minted; null when there is none to give (a kept feed, or the backend said no)
  const [code, setCode] = useState<string | null | undefined>(undefined);
  const [copied, setCopied] = useState<'no' | 'yes' | 'by_hand'>('no');
  useEffect(() => {
    let live = true;
    handoffForMove().then((c) => { if (live) setCode(c); }, () => { if (live) setCode(null); });
    return () => { live = false; };
  }, []);
  // to the road, wherever this was offered: the screen it was offered on means nothing in a fresh browser
  const link = (via: HandoffVia) => withHandoff(`${window.location.origin}/`, code, via);
  useEffect(() => {
    if (!code) return;
    if (os === 'ios') window.history.replaceState(window.history.state, '', link('menu'));
    track('handoff_offered', { via: os === 'ios' ? 'menu' : os === 'android' ? 'chrome' : 'copy', from, os, in_app: kind ?? 'none', uid: currentUid() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (code === undefined) return <p className="hint">{MOVE.minting}</p>;
  if (!code) return null;
  const copy = async () => {
    track('handoff_tapped', { via: 'copy', from, os, uid: currentUid() });
    try { await navigator.clipboard.writeText(link('copy')); setCopied('yes'); } catch { setCopied('by_hand'); }
  };
  const [t1, menu, t2, open, t3] = MOVE.menu;
  return (
    <div className="stack move-to-browser" style={{ gap: 8 }}>
      <p className="hint">{MOVE.lead(browser)}</p>
      {os === 'ios' && <p className="why" style={{ margin: 0 }}>{t1}<b>{menu}</b>{t2}<b>{open}</b>{t3}</p>}
      {os === 'android' && (
        <a className="btn small" href={chromeIntentUrl(link('chrome'))} onClick={() => track('handoff_tapped', { via: 'chrome', from, os, uid: currentUid() })} style={{ width: '100%', textAlign: 'center' }}>{MOVE.chrome}</a>
      )}
      {copied === 'by_hand'
        ? <><p className="hint">{MOVE.copyByHand}</p><input className="input" readOnly value={link('copy')} aria-label="Your link" onFocus={(e) => e.currentTarget.select()} /></>
        : <button type="button" className="btn small ghost" onClick={copy} style={{ width: '100%' }}>{copied === 'yes' ? MOVE.copied(browser) : MOVE.copy}</button>}
    </div>
  );
}
